import { DateTime } from 'luxon';
import { z } from 'zod/v4-mini';

import {
  ConditionType,
  MedicationSchema,
  conditionTypeSchema,
  medicationSchemeSchema,
} from '@/models/ConditionModel.ts';
import { FileModel, fileSchema } from '@/models/FileModel.ts';
import {
  TeleMonitoringCriteria,
  telemonitoringCriteriaSchema,
} from '@/models/TelemonitoringCriteriaModel.ts';
import { Schema, cz } from '@/utils/zodUtils.ts';

type CarePlanDuration = '1' | '2' | '3';

export type Type1PenCarePlanCreationInfo = {
  diabetesType: 'type1';
  insulinScheme: 'insulinPen';
  homeServiceProviderId: undefined;
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type Type2PenCarePlanCreationInfo = {
  diabetesType: 'type2';
  insulinScheme: 'insulinPen';
  homeServiceProviderId: undefined;
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type Type1PumpCarePlanCreationInfo = {
  diabetesType: 'type1';
  insulinScheme: 'insulinPump';
  homeServiceProviderId: string;
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type Type2PumpCarePlanCreationInfo = {
  diabetesType: 'type2';
  insulinScheme: 'insulinPump';
  homeServiceProviderId: string;
  termDate: undefined;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type GestationalPenCarePlanCreationInfo = {
  diabetesType: 'gestational';
  insulinScheme: 'insulinPen';
  homeServiceProviderId: undefined;
  termDate: string;
  inclusionCriteria: TeleMonitoringCriteria;
  duration: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

export type GestationalNoInsulinCarePlanCreationInfo = {
  diabetesType: 'gestational';
  insulinScheme: 'noInsulin';
  termDate: string;
};

export type CarePlanCreationInfoWithPrescription =
  | Type1PenCarePlanCreationInfo
  | Type2PenCarePlanCreationInfo
  | Type1PumpCarePlanCreationInfo
  | Type2PumpCarePlanCreationInfo
  | GestationalPenCarePlanCreationInfo;

export type CarePlanCreationInfo =
  | CarePlanCreationInfoWithPrescription
  | GestationalNoInsulinCarePlanCreationInfo;

export type SimpleCarePlanCreationInfo = {
  diabetesType: ConditionType;
  insulinScheme: MedicationSchema;
  homeServiceProviderId?: string;
  termDate?: string;
  inclusionCriteria?: TeleMonitoringCriteria;
  duration?: CarePlanDuration;
  description?: string;
  descriptionAttachment?: FileModel;
};

const cannotBeType12AndNoInsulin = (data: SimpleCarePlanCreationInfo) => {
  if (data.diabetesType === 'type1' || data.diabetesType === 'type2') {
    return data.insulinScheme !== 'noInsulin';
  }
  return true;
};

const cannotBeGestationalAndUsePump = (data: SimpleCarePlanCreationInfo) => {
  if (data.diabetesType === 'gestational') {
    return data.insulinScheme !== 'insulinPump';
  }
  return true;
};

const requiredHSPIfPump = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme === 'insulinPump') {
    return !!data.homeServiceProviderId;
  }
  return true;
};

const requiredTermDateIfGestational = (data: SimpleCarePlanCreationInfo) => {
  if (data.diabetesType === 'gestational') {
    return !!data.termDate;
  }
  return true;
};

const requiredInclusionCriteriaIfInsulinated = (
  data: SimpleCarePlanCreationInfo,
) => {
  if (data.insulinScheme !== 'noInsulin') {
    return !!data.inclusionCriteria;
  }
  return true;
};

const requiredDurationIfInsulinated = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme !== 'noInsulin') {
    return !!data.duration;
  }
  return true;
};

const requiredDescriptionIfInsulinated = (data: SimpleCarePlanCreationInfo) => {
  if (data.insulinScheme !== 'noInsulin') {
    return !!data.description || !!data.descriptionAttachment;
  }
  return true;
};

export const carePlanCreationSchema: Schema<SimpleCarePlanCreationInfo> = z
  .object(
    {
      diabetesType: conditionTypeSchema,
      insulinScheme: medicationSchemeSchema,
      homeServiceProviderId: z.optional(z.string()),
      termDate: z.optional(
        z.iso
          .date('form.required')
          .check(cz.validation.dateAfter(DateTime.now(), 'form.date.past')),
      ),
      inclusionCriteria: z.optional(telemonitoringCriteriaSchema),
      duration: z.optional(z.literal(['1', '2', '3'])),
      description: z.optional(z.string()),
      descriptionAttachment: z.optional(fileSchema),
    },
    'forms.required',
  )
  .check(
    z.refine(cannotBeType12AndNoInsulin, {
      path: ['insulinSchema'],
      error: 'form.required',
    }),
    z.refine(cannotBeGestationalAndUsePump, {
      path: ['insulinSchema'],
      error: 'form.required',
    }),
    z.refine(requiredHSPIfPump, {
      path: ['homeServiceProviderId'],
      error: 'form.required',
    }),
    z.refine(requiredTermDateIfGestational, {
      path: ['termDate'],
      error: 'form.required',
    }),
    z.refine(requiredInclusionCriteriaIfInsulinated, {
      path: ['inclusionCriteria'],
      error: 'form.required',
    }),
    z.refine(requiredDurationIfInsulinated, {
      path: ['duration'],
      error: 'form.required',
    }),
    z.refine(requiredDescriptionIfInsulinated, {
      path: ['description'],
      error: 'diabetesForms.eitherObjectiveOrFile',
    }),
  );
